import React from 'react';
import { styles } from './ServicesInfoBlock.css';
import { Icon, IconNameEnum } from '@jbhi-fi/lanyard-ui/components/Icon';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';
import { OfferingsProps } from '@jbhi-fi/apis/src/services/types';
import Checkbox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { useIdPrefix } from '../../IdPrefix';
import { ActionButton } from '../../ActionButton';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export const INSTALL_ID_PREFIX = 'install-';

export type ServicesInfoBlockProps = {
  shouldShowActionButtons?: boolean;
  heading: string;
  headingLogoURL?: string | null;
  headingLogoName?: IconNameEnum | keyof typeof IconNameEnum;
  headingLogoSize?: PossiblyResponsiveValue<{ width: number; height: number }>;
  headingFontSize?: 'normal' | 'larger';
  subText?: string;
  variant?: 'checkbox' | 'button';
  selectedOfferingId?: string;
  features?: Array<string>;
  offerings: Array<
    OfferingsProps & {
      heading?: string;
      subText?: string;
      features?: Array<string>;
    }
  >;
  onAdd: (event: React.ChangeEvent | React.SyntheticEvent, offeringId: string) => void;
  onRemove: (offeringId?: string) => void;
  buttonsProps?: Array<{
    offeringId: string;
    heading: string;
    price: number;
    elementId: string;
  }>;
  buttonPosition?: 'bottom' | 'right';
  isCartReady?: boolean;
  isExtraCare?: boolean;
};

const Wrapper = ({
  heading,
  headingLogoName,
  headingLogoURL,
  headingLogoSize,
  headingFontSize,
  subText,
  testIdPrefix,
  children,
}: Pick<ServicesInfoBlockProps, 'heading' | 'headingLogoName' | 'subText' | 'headingLogoURL' | 'headingFontSize'> & {
  children: React.ReactNode;
  headingLogoSize: PossiblyResponsiveValue<{ width: number; height: number }>;
  testIdPrefix?: string;
}) => {
  const { height: logoHeight, width: logoWidth } = useResponsiveValue(headingLogoSize);
  const isMobile = useBreakpoint('md', 'down');

  return (
    <div className={styles.wrapperContainer} data-testid={`${testIdPrefix}service-block`}>
      <div className={styles.headingContainer} data-testid={`${testIdPrefix}service-heading-container`}>
        {headingLogoURL ? (
          <img
            src={headingLogoURL}
            alt="headingLogo"
            data-testid={`${testIdPrefix}service-icon`}
            className={styles.headingIcon}
            height={logoHeight}
            width={logoWidth}
          />
        ) : headingLogoName ? (
          <Icon
            name={headingLogoName}
            data-testid={`${testIdPrefix}service-icon`}
            className={styles.headingIcon}
            height={logoHeight}
            width={logoWidth}
          />
        ) : null}
        <CalloutFont
          className={styles.heading}
          as={headingFontSize === 'larger' && !isMobile ? 'h2' : 'h3'}
          testId={`${testIdPrefix}service-heading`}
        >
          {heading}
        </CalloutFont>
      </div>
      {subText ? (
        <p className={styles.subText} data-testid={`${testIdPrefix}service-sub-text`}>
          {subText}
        </p>
      ) : null}
      {children}
    </div>
  );
};

export const ServicesInfoBlock = ({
  shouldShowActionButtons,
  heading,
  headingLogoSize = { width: 30, height: 30 },
  headingLogoURL,
  subText,
  selectedOfferingId,
  onAdd,
  onRemove,
  isCartReady = true,
  isExtraCare,
  ...rest
}: ServicesInfoBlockProps) => {
  const { offerings } = rest;
  const testIdPrefix = useIdPrefix() || '';

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    offering: OfferingsProps & {
      heading?: string | undefined;
      subText?: string | undefined;
      features?: Array<string> | undefined;
    }
  ) => {
    if (selectedOfferingId === offering.id) {
      onRemove(offering.id);
    } else {
      onAdd(event, offering.id);
    }
  };

  const serviceInfoPrefix = isExtraCare ? `service-info-` : 'service-';
  return (
    <Wrapper
      heading={heading}
      headingLogoURL={headingLogoURL}
      headingLogoSize={headingLogoSize}
      headingFontSize="normal"
      subText={subText}
      testIdPrefix={testIdPrefix}
    >
      {offerings?.map(offering => {
        return (
          <div
            key={offering.id}
            className={styles.checkboxContainer}
            data-testid={`${testIdPrefix}${serviceInfoPrefix}checkbox-container`}
          >
            {isCartReady ? (
              !shouldShowActionButtons && (
                <Checkbox
                  name="add-service"
                  label=""
                  disabled={!isCartReady}
                  onChange={e => handleChange(e, offering)}
                  checked={selectedOfferingId === offering.id}
                  className={`ServicesInfoBlock__checkbox ${styles.checkbox}`}
                  data-testid={`${testIdPrefix}${serviceInfoPrefix}checkbox`}
                  id="service-checkbox"
                />
              )
            ) : (
              <Icon name="Loading" height={27} width={27} className={styles.checkboxLoading} />
            )}
            <div
              className={styles.contentContainer}
              data-testid={`${testIdPrefix}${serviceInfoPrefix}${isExtraCare ? 'content-container' : 'details'}`}
            >
              <button
                key={offering.id}
                onClick={e => onAdd(e, offering.id)}
                className={styles.contentHeading}
                data-testid={`${testIdPrefix}${serviceInfoPrefix}heading`}
              >
                {offering.heading}
              </button>
              {isExtraCare && offering.subText ? (
                <p className={styles.contentSubText} data-testid={`${testIdPrefix}${serviceInfoPrefix}sub-text`}>
                  {offering.subText}
                </p>
              ) : null}
              <ul className={styles.list} data-testid={`${testIdPrefix}${serviceInfoPrefix}list`}>
                {offering?.shortServiceFeatures?.map((feature, i) => (
                  <li key={i} className={styles.listItem}>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className={styles.moreInfoLink}
                onClick={e => onAdd(e, offering.id)}
                data-testid={`${testIdPrefix}${serviceInfoPrefix}${isExtraCare ? 'link' : '-more-info-button'}`}
              >
                More information
              </button>

              {shouldShowActionButtons ? (
                <>
                  <div className={styles.buttonsWrapper}>
                    {onRemove ? <ActionButton type="secondary" label="No thanks" onClick={onRemove} /> : null}
                    <ActionButton type="action" label="Add to cart" onClick={e => handleChange(e, offering)} />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        );
      })}
    </Wrapper>
  );
};
